/**
 * Formats d’affichage — un seul endroit décide de la forme des dates,
 * des montants et des nombres (NF-06, NF-07).
 *
 *  - **Dates** : stockées en UTC (`timestamptz`), affichées en `Europe/Zurich`
 *    au format `jj.mm.aaaa HH:MM` (ARCHITECTURE §3). Aucun écran ne doit
 *    appeler `toLocaleString` directement : un soir de changement d’heure, la
 *    représentation de 20h00 doit rester à 20h00.
 *  - **Argent** : centimes entiers formatés par `@tb/shared/money`
 *    (`1'234.50`). Le composant `<Money>` s’appuie sur ces fonctions.
 */

import {
  formatCHF,
  formatCHFCourt,
  grouperMilliers,
  SEPARATEUR_DECIMAL,
  type OptionsFormatCHF,
} from '@tb/shared'

import { LOCALE_INTL } from '@tb/shared'

import { langueCourante } from './langue'

/** Fuseau d’affichage de toute l’application. */
export const FUSEAU = 'Europe/Zurich'

/**
 * ★★ I18N-01 — LA LOCALE SUIT LA LANGUE, ET LES TROIS SONT SUISSES.
 *
 * `LOCALE` était `'fr-CH'` en dur. Elle est désormais lue à chaque appel dans
 * `LOCALE_INTL[langueCourante()]` — `fr-CH`, `de-CH` ou `en-CH`.
 *
 * ★ **Les trois sont suisses, et c'est ce qui compte le plus ici.** Servir
 *   `de-DE` donnerait `4.9.2026` et `en-US` donnerait `9/4/2026` : une date
 *   américaine sur un billet suisse inverse le jour et le mois pour les douze
 *   premiers jours de chaque mois, sans que rien ne le signale. C'est le genre
 *   de défaut qui ne se découvre qu'au contrôle, quand quelqu'un se présente
 *   le mauvais soir. Le fuseau, lui, reste `Europe/Zurich` en toute langue.
 *
 * ⚠️ Ce qui change réellement d'une langue à l'autre est donc étroit et
 *    précis : les NOMS de jours et de mois (« jeudi » / « Donnerstag »), et
 *    l'ordre des mots dans `formatJourLong`. Les chiffres ne bougent pas.
 */
export function localeCourante(): string {
  return LOCALE_INTL[langueCourante()]
}

/** Ce qu’on affiche à la place d’une valeur absente. */
export const ABSENT = '—'

export type EntreeDate = string | number | Date | null | undefined

/* -------------------------------------------------------------------------- */
/* Socle : décomposer un instant dans le fuseau du théâtre                    */
/* -------------------------------------------------------------------------- */

/** Les formateurs `Intl` sont coûteux à construire : on les garde. */
const memoFormateurs = new Map<string, Intl.DateTimeFormat>()

function formateur(cle: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  /* ⚠️ La locale entre dans la CLÉ du cache. Sans cela, le premier formateur
     construit (en français, au premier rendu) resterait servi à toute la page
     après un changement de langue : les dates seraient restées françaises au
     milieu d'un écran allemand, et la mémoïsation aurait transformé un
     réglage en défaut permanent. */
  const locale = localeCourante()
  const cleComplete = `${locale}|${cle}`
  let existant = memoFormateurs.get(cleComplete)
  if (existant === undefined) {
    existant = new Intl.DateTimeFormat(locale, { timeZone: FUSEAU, ...options })
    memoFormateurs.set(cleComplete, existant)
  }
  return existant
}

const formateurParties = new Intl.DateTimeFormat('en-US', {
  timeZone: FUSEAU,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

interface PartiesDate {
  readonly annee: number
  readonly mois: number
  readonly jour: number
  readonly heure: number
  readonly minute: number
  readonly seconde: number
}

/** Décompose un instant en composantes **locales de Zurich**. */
function parties(date: Date): PartiesDate {
  const brut: Record<string, string> = {}
  for (const partie of formateurParties.formatToParts(date)) {
    if (partie.type !== 'literal') brut[partie.type] = partie.value
  }
  return {
    annee: Number(brut.year ?? '0'),
    mois: Number(brut.month ?? '1'),
    jour: Number(brut.day ?? '1'),
    heure: Number(brut.hour ?? '0'),
    minute: Number(brut.minute ?? '0'),
    seconde: Number(brut.second ?? '0'),
  }
}

/** Convertit une entrée quelconque en `Date` valide, ou `null`. */
export function versDate(valeur: EntreeDate): Date | null {
  if (valeur === null || valeur === undefined || valeur === '') return null
  const date = valeur instanceof Date ? valeur : new Date(valeur)
  return Number.isNaN(date.getTime()) ? null : date
}

function deuxChiffres(valeur: number): string {
  return String(valeur).padStart(2, '0')
}

/* -------------------------------------------------------------------------- */
/* Dates et heures                                                            */
/* -------------------------------------------------------------------------- */

/** `12.10.2027` */
export function formatDate(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ABSENT
  const p = parties(date)
  return `${deuxChiffres(p.jour)}.${deuxChiffres(p.mois)}.${String(p.annee)}`
}

/** `20:00` */
export function formatHeure(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ABSENT
  const p = parties(date)
  return `${deuxChiffres(p.heure)}:${deuxChiffres(p.minute)}`
}

/** `12.10.2027 20:00` — le format de référence (ARCHITECTURE §3). */
export function formatDateHeure(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ABSENT
  return `${formatDate(date)} ${formatHeure(date)}`
}

/** `mardi 12 octobre 2027` — en-têtes d’écran, listes d’émargement. */
export function formatJourLong(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ABSENT
  return formateur('jourLong', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

/** `mar. 12.10.2027 20:00` — listes de représentations. */
export function formatJourCourt(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ABSENT
  const jourSemaine = formateur('jourCourt', { weekday: 'short' }).format(date)
  return `${jourSemaine} ${formatDate(date)} ${formatHeure(date)}`
}

/**
 * Date seule `AAAA-MM-JJ` (ouverture de campagne — CAM-01) → `jj.mm.aaaa`.
 * Traitée comme du texte, **sans fuseau** : une date d’ouverture n’a pas d’heure
 * et ne doit jamais reculer d’un jour à cause d’un décalage horaire.
 */
export function formatDateSeule(valeur: string | null | undefined): string {
  if (valeur === null || valeur === undefined || valeur === '') return ABSENT
  const correspondance = /^(\d{4})-(\d{2})-(\d{2})/u.exec(valeur)
  if (correspondance === null) return ABSENT
  const [, annee, mois, jour] = correspondance
  return `${jour ?? ''}.${mois ?? ''}.${annee ?? ''}`
}

/** L’instant est-il passé ? (représentation jouée, lien magique expiré…) */
export function estPassee(valeur: EntreeDate, reference: Date = new Date()): boolean {
  const date = versDate(valeur)
  return date !== null && date.getTime() < reference.getTime()
}

/* -------------------------------------------------------------------------- */
/* Champs de formulaire                                                       */
/* -------------------------------------------------------------------------- */

/**
 * ISO UTC → valeur d’un `<input type="datetime-local">`, **en heure de Zurich**.
 * `2027-10-12T18:00:00Z` → `2027-10-12T20:00` (heure d’été).
 */
export function versEntreeDateHeure(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ''
  const p = parties(date)
  return `${String(p.annee)}-${deuxChiffres(p.mois)}-${deuxChiffres(p.jour)}T${deuxChiffres(p.heure)}:${deuxChiffres(p.minute)}`
}

/**
 * Valeur d’un `<input type="datetime-local">` (heure de Zurich) → ISO UTC,
 * prêt à partir dans un DTO.
 *
 * Deux passes pour traverser correctement les changements d’heure : la première
 * estime le décalage, la seconde le corrige si l’estimation tombait de l’autre
 * côté de la bascule.
 *
 * @throws RangeError si la saisie n’est pas au format attendu.
 */
export function depuisEntreeDateHeure(valeur: string): string {
  const correspondance = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/u.exec(valeur)
  if (correspondance === null) {
    throw new RangeError(`Date invalide : « ${valeur} »`)
  }
  const [, a = '0', m = '1', j = '1', h = '0', min = '0'] = correspondance
  const naif = Date.UTC(Number(a), Number(m) - 1, Number(j), Number(h), Number(min))

  let instant = naif - decalageZurich(naif)
  instant = naif - decalageZurich(instant)
  return new Date(instant).toISOString()
}

/** Décalage `Europe/Zurich` − UTC, en millisecondes, à l’instant donné. */
function decalageZurich(instant: number): number {
  const p = parties(new Date(instant))
  const equivalentUtc = Date.UTC(p.annee, p.mois - 1, p.jour, p.heure, p.minute, p.seconde)
  return equivalentUtc - instant
}

/** ISO / `Date` → valeur d’un `<input type="date">` (`AAAA-MM-JJ`, Zurich). */
export function versEntreeDate(valeur: EntreeDate): string {
  const date = versDate(valeur)
  if (date === null) return ''
  const p = parties(date)
  return `${String(p.annee)}-${deuxChiffres(p.mois)}-${deuxChiffres(p.jour)}`
}

/** Maintenant, au format d’un `<input type="datetime-local">`. */
export function maintenantEntreeDateHeure(): string {
  return versEntreeDateHeure(new Date())
}

/* -------------------------------------------------------------------------- */
/* Argent                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Centimes → `1'234.50`. Fin de chaîne : `@tb/shared/money`, qui garantit qu’on
 * ne formate jamais autre chose qu’un entier de centimes.
 */
export function formatMontant(
  centimes: number | null | undefined,
  options?: OptionsFormatCHF,
): string {
  if (centimes === null || centimes === undefined) return ABSENT
  return formatCHF(centimes, options ?? {})
}

/** Centimes → `45.–` quand le montant est rond (grilles tarifaires). */
export function formatMontantCourt(centimes: number | null | undefined): string {
  if (centimes === null || centimes === undefined) return ABSENT
  return formatCHFCourt(centimes)
}

/* -------------------------------------------------------------------------- */
/* Nombres et texte                                                           */
/* -------------------------------------------------------------------------- */

/**
 * `1234` → `1'234` — même apostrophe suisse (U+0027) que `formatCHF`.
 *
 * Écrit à la main plutôt qu'avec `Intl.NumberFormat('fr-CH')` : le
 * groupement de milliers d'ICU dépend de la version du moteur (apostrophe sur
 * certaines, espace insécable sur d'autres), ce qui ferait cohabiter deux
 * conventions différentes sur le même écran qu'un montant en centimes.
 */
export function formatNombre(valeur: number | null | undefined): string {
  if (valeur === null || valeur === undefined || !Number.isFinite(valeur)) return ABSENT
  return grouperMilliers(Math.round(valeur))
}

/**
 * Taux de reconduction, remplissage… `0.734` → `73.4%`. Même séparateur
 * décimal (`.`) et même groupement de milliers que `formatCHF`, pour la même
 * raison : `Intl.NumberFormat('fr-CH')` donnerait une virgule décimale, une
 * troisième convention numérique sur l'écran.
 */
export function formatPourcent(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return ABSENT
  const arrondi = Math.round(ratio * 1000) / 10
  const [entiere, decimale] = Math.abs(arrondi).toFixed(1).split('.')
  const signe = arrondi < 0 ? '-' : ''
  const partieDecimale = decimale === '0' ? '' : `${SEPARATEUR_DECIMAL}${decimale}`
  return `${signe}${grouperMilliers(Number(entiere))}${partieDecimale}%`
}

/** `part / total` en pourcentage, sans division par zéro. */
export function formatPart(part: number, total: number): string {
  if (total <= 0) return ABSENT
  return formatPourcent(part / total)
}

/**
 * Accord en nombre : `pluriel(37, 'proposition')` → `propositions`.
 * Le pluriel irrégulier se passe en troisième argument (`'bail', 'baux'`).
 */
export function pluriel(compte: number, singulier: string, formePluriel?: string): string {
  return Math.abs(compte) >= 2 ? (formePluriel ?? `${singulier}s`) : singulier
}

/**
 * `quantite(37, 'proposition')` → `37 propositions`.
 * C’est la brique des récapitulatifs chiffrés des `ConfirmDialog`
 * (ARCHITECTURE §9.4) : « 37 propositions vont expirer, 61 sièges libérés ».
 */
export function quantite(compte: number, singulier: string, formePluriel?: string): string {
  return `${formatNombre(compte)} ${pluriel(compte, singulier, formePluriel)}`
}

/* La conjonction change de langue (« et » / « und » / « and ») : le formateur
   se construit donc par langue, comme ceux des dates. */
const memoListes = new Map<string, Intl.ListFormat>()

function formateurListe(): Intl.ListFormat {
  const locale = localeCourante()
  let existant = memoListes.get(locale)
  if (existant === undefined) {
    existant = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' })
    memoListes.set(locale, existant)
  }
  return existant
}

/** `['F12', 'F13']` → `F12 et F13`. */
export function formatListe(elements: readonly string[]): string {
  if (elements.length === 0) return ABSENT
  return formateurListe().format(elements)
}

/**
 * Initiales pour une pastille d’en-tête.
 * `('Dubuis', 'Marie')` → `MD` · `('Anne Praz')` → `AP` · `('Luc')` → `LU`.
 */
export function initiales(nom: string, prenom?: string): string {
  if (prenom !== undefined && prenom.trim() !== '') {
    return `${prenom.trim().charAt(0)}${nom.trim().charAt(0)}`.toUpperCase()
  }
  const mots = nom
    .trim()
    .split(/\s+/u)
    .filter((mot) => mot !== '')
  const premier = mots[0]
  if (premier === undefined) return '?'
  if (mots.length === 1) return premier.slice(0, 2).toUpperCase()
  return `${premier.charAt(0)}${mots[mots.length - 1]?.charAt(0) ?? ''}`.toUpperCase()
}

/** Tronque proprement un texte long (notes, motifs) pour une cellule de tableau. */
export function tronquer(texte: string, longueur = 80): string {
  return texte.length <= longueur ? texte : `${texte.slice(0, longueur - 1).trimEnd()}…`
}

/* -------------------------------------------------------------------------- */
/* Élisions et contractions — « de » devant un nom propre                     */
/* -------------------------------------------------------------------------- */

/**
 * ★★ « REPRÉSENTATIONS DE LE MISANTHROPE ».
 *
 * Un titre d'œuvre porte souvent son article, et concaténer `de ${titre}`
 * produit alors du français cassé : « de Le Misanthrope », « de Les Fourberies
 * de Scapin », « de Un fil à la patte ». Cela s'affiche dans les légendes de
 * tableaux — donc précisément dans ce qu'une lectrice d'écran prononce à voix
 * haute, où la faute ne peut pas être ignorée du regard.
 *
 * Les cinq cas, dans l'ordre où on les rencontre :
 *
 *   « Le Misanthrope »          → **du** Misanthrope
 *   « Les Fourberies… »         → **des** Fourberies…
 *   « La Cantatrice chauve »    → **de la** Cantatrice chauve
 *   « L'Avare »                 → **de l'**Avare
 *   « Un fil à la patte »       → **d'**Un fil à la patte  (voyelle initiale)
 *   « Cyrano de Bergerac »      → **de** Cyrano de Bergerac
 *
 * La casse de l'article est respectée telle qu'elle est saisie : c'est le
 * théâtre qui écrit ses titres, pas nous.
 *
 * ⚠️ Le « h » aspiré (« la Hongrie », « le hall ») n'est pas détectable sans
 * dictionnaire ; on élide donc devant tout `h`, ce qui est juste pour la
 * grande majorité des titres (« d'Huis clos ») et faux pour de rares
 * exceptions. Une faute rare vaut mieux qu'une faute systématique.
 */
export function deTitre(titre: string): string {
  const nu = titre.trim()
  if (nu === '') return 'de'

  const apostrophe = /^l['’]\s*/iu
  if (apostrophe.test(nu)) return `de l’${nu.replace(apostrophe, '')}`
  if (/^les\s+/iu.test(nu)) return `des ${nu.replace(/^les\s+/iu, '')}`
  if (/^le\s+/iu.test(nu)) return `du ${nu.replace(/^le\s+/iu, '')}`
  if (/^la\s+/iu.test(nu)) return `de la ${nu.replace(/^la\s+/iu, '')}`
  if (/^[aàâeéèêëiîïoôöuùûühyAÀÂEÉÈÊËIÎÏOÔÖUÙÛÜHY]/u.test(nu)) return `d’${nu}`
  return `de ${nu}`
}
